/* KpiSparklines — row of 4 small KPI tiles, each with a tiny sparkline.
   Static seeded "this week" values; sparkline drawn as inline SVG. */

import type { JobPosting } from '@/modules/job/services/job.api';

export interface KpiSparklinesProps {
    jobs: JobPosting[];
    accent?: string;
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

function buildSeries(seed: number, base: number, drift: number) {
    const out: number[] = [];
    let v = base;
    for (let i = 0; i < 12; i++) {
        v += ((seed >> (i % 5)) & 1 ? 1 : -1) * drift + (i % 3) * 1.5;
        v = Math.max(1, v);
        out.push(Math.round(v));
    }
    return out;
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
    const w = 70;
    const h = 24;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const points = values.map((v, i) => {
        const x = (i / (values.length - 1)) * w;
        const y = h - ((v - min) / range) * h;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
            <polyline
                points={points.join(' ')}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle cx={points[points.length - 1]?.split(',')[0]} cy={points[points.length - 1]?.split(',')[1]} r={2} fill={color} />
        </svg>
    );
}

export function KpiSparklines({
    jobs,
    accent = '#fd775e',
    surface = 'rgba(255,255,255,0.7)',
    text = '#2a241e',
    muted = '#6f6660',
    rule = 'rgba(120, 90, 50, 0.08)',
}: KpiSparklinesProps) {
    const seed = jobs[0] ? hash(jobs[0]._id) : 17;
    const data = [
        { label: 'Sourced', value: 142 + (seed % 30), delta: '+12%', deltaPositive: true, color: 'rgb(0 132 255)', series: buildSeries(seed, 12, 3) },
        { label: 'Screened', value: 89 + (seed % 18), delta: '+8%', deltaPositive: true, color: 'rgb(20 143 63)', series: buildSeries(seed + 11, 9, 2) },
        { label: 'Outreach', value: 47 + (seed % 12), delta: '+24%', deltaPositive: true, color: 'rgb(192 88 11)', series: buildSeries(seed + 22, 5, 2) },
        { label: 'Reports drafted', value: 14 + (seed % 5), delta: '−3%', deltaPositive: false, color: 'rgb(108 70 145)', series: buildSeries(seed + 33, 3, 1) },
    ];

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
            {data.map((kpi) => (
                <div
                    key={kpi.label}
                    style={{
                        background: surface,
                        border: `1px solid ${rule}`,
                        borderRadius: 14,
                        padding: '14px 16px',
                        position: 'relative',
                        fontFamily: 'var(--v2-font-sans)',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: muted, fontWeight: 500 }}>{kpi.label}</span>
                        <span style={{ fontSize: 10, color: kpi.deltaPositive ? 'rgb(20 143 63)' : 'rgb(192 88 11)', fontWeight: 600 }}>
                            {kpi.delta}
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6 }}>
                        <span style={{
                            fontFamily: 'var(--v2-font-serif)',
                            fontSize: 26,
                            fontWeight: 400,
                            letterSpacing: '-0.02em',
                            lineHeight: 1,
                            color: text,
                        }}>
                            {kpi.value}
                        </span>
                        <Sparkline values={kpi.series} color={kpi.color} />
                    </div>
                    <div style={{ fontSize: 10, color: muted, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        this week
                    </div>
                </div>
            ))}
        </div>
    );
}
